import type {
    ExtendedDateTime,
    DayOfWeek,
    FlexibleWorkingHours,
    FlexibleWorkingHoursView,
    ListFlexibleWorkingHoursResponse,
    WorkLocationType,
    Maybe,
    LocationSourceType,
    LocationIdType,
    LocationEntityType,
    Location,
} from 'owa-graph-schema';
import { getUserEmailAddress } from 'accelerator-aad/lib/getUserEmailAddress';
import { GetFlexibleWorkingHoursForEmployeeDocument } from 'hybridspace-graphql/lib/graphql/__generated__/GetFlexibleWorkingHoursForEmployeeQuery.interface';
import { RecurringFlexibleWorkingHoursQueryDocument } from 'hybridspace-graphql/lib/graphql/__generated__/recurringFlexibleWorkingHoursQuery.interface';
import { logUsage } from 'owa-analytics';
import { query } from 'owa-apollo';
import {
    addDays,
    addMinutes,
    getDay,
    getISOString,
    isBefore,
    type OwaDate,
    userDate,
} from 'owa-datetime';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import getFlexibleWorkingHoursSummaries from './getFlexibleWorkingHoursSummaries';

import type { DateRange } from 'owa-datetime-utils';
import type { WorkLocationSurfaceType } from 'owa-calendar-working-hours-location-utils';

export default async function fetchFWHForWeek(currentCalendarDateRangeView: DateRange): Promise<
    | {
          summaries: never[];
          recurringFlexibleWorkingHours: {
              __typename?: 'ListFlexibleWorkingHoursResponse';
              segments?: Array<{
                  __typename?: 'FlexibleWorkingHours';
                  id: string;
                  workLocationType: WorkLocationType;
                  start: {
                      __typename?: 'ExtendedDateTime';
                      dateTime: string;
                      timeZone: {
                          __typename?: 'TimeZoneWH';
                          name: string;
                      };
                  };
                  end: {
                      __typename?: 'ExtendedDateTime';
                      dateTime: string;
                      timeZone: {
                          __typename?: 'TimeZoneWH';
                          name: string;
                      };
                  };
                  recurrenceDetailsWH?: {
                      __typename?: 'RecurrenceDetailsWH';
                      recurrence: {
                          __typename?: 'PatternedRecurrenceWH';
                          recurrencePattern?: {
                              __typename?: 'RecurrencePatternWH';
                              weeklyRecurrencePattern: {
                                  __typename?: 'WeeklyRecurrencePatternWH';
                                  interval: number;
                                  firstDayOfWeek: DayOfWeek;
                                  daysOfWeek: Array<DayOfWeek>;
                              };
                          } | null;
                          recurrenceRange?: {
                              __typename?: 'RecurrenceRangeWH';
                              endDateRecurrenceRange?: {
                                  __typename?: 'EndDateRecurrenceRangeWH';
                                  start?: {
                                      __typename?: 'ExtendedDateTime';
                                      dateTime: string;
                                      timeZone: {
                                          __typename?: 'TimeZoneWH';
                                          name: string;
                                      };
                                  } | null;
                                  end?: {
                                      __typename?: 'ExtendedDateTime';
                                      dateTime: string;
                                      timeZone: {
                                          __typename?: 'TimeZoneWH';
                                          name: string;
                                      };
                                  } | null;
                              } | null;
                              noEndRecurrenceRange?: {
                                  __typename?: 'NoEndRecurrenceRangeWH';
                                  start?: {
                                      __typename?: 'ExtendedDateTime';
                                      dateTime: string;
                                      timeZone: {
                                          __typename?: 'TimeZoneWH';
                                          name: string;
                                      };
                                  } | null;
                              } | null;
                          } | null;
                      };
                      timeZone: {
                          __typename?: 'TimeZoneWH';
                          name: string;
                      };
                  } | null;
                  workLocationDetails?: {
                      __typename?: 'Location';
                      id?: string | null;
                      idType?: LocationIdType | null;
                      displayName?: string | null; // if we do not find a matching day for recurring and occurring
                      annotation?: string | null;
                      locationType?: LocationEntityType | null;
                      locationUri?: string | null;
                      locationSource?: LocationSourceType | null;
                  } | null;
              }> | null;
          };
          occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
      }
    | {
          summaries: {
              date: OwaDate;
              timestamp: number;
              workLocationSurfaceType: WorkLocationSurfaceType;
              buildingId?: Location['id'];
              buildingIds: Map<Location['id'], Location['displayName']>;
              segments: {
                  start: ExtendedDateTime;
                  end: ExtendedDateTime;
                  workLocationType: WorkLocationType;
                  workLocationDetails?: Maybe<Location> | undefined;
              }[];
          }[];
          recurringFlexibleWorkingHours: ListFlexibleWorkingHoursResponse;
          occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
      }
> {
    const queryStartDate = currentCalendarDateRangeView.start;
    const queryEndDate = addDays(
        currentCalendarDateRangeView.end,
        1 /* add one day to create the range */
    );

    const invalidRange = isBefore(queryEndDate, queryStartDate);
    if (invalidRange) {
        logUsage(
            'Places_useEmployeeFlexibleWorkingHours_rangeError',
            getModuleContextMailboxInfo(),
            {
                message: `Invalid dateRange for ${queryStartDate} to ${queryEndDate}`,
            }
        );
        throw new Error('Invalid dateRange');
    }

    // getting the work location for a particular day [startDate, endDate)
    const { data: occurrenceDetails, error: occurrenceDetailsError } = await query(
        GetFlexibleWorkingHoursForEmployeeDocument,
        {
            variables: {
                input: {
                    mailboxId: getUserEmailAddress(),
                    startView: getISOString(queryStartDate), // start of the week
                    endView: getISOString(queryEndDate), // end of the week
                },
            },
        }
    );
    if (occurrenceDetailsError) {
        throw new Error("could not get the user's occurring flexible working hours");
    }

    const flexibleWorkingHours: FlexibleWorkingHoursView[] =
        occurrenceDetails?.flexibleWorkingHoursView?.segments ?? [];

    const modifiedOccurringFlexibleWorkingHours = [...flexibleWorkingHours];
    const { data: recurringDetails, error: recurringDetailsError } = await query(
        RecurringFlexibleWorkingHoursQueryDocument,
        {
            variables: {
                input: {
                    mailboxId: getModuleContextMailboxInfo().mailboxSmtpAddress,
                },
            },
        }
    );
    if (recurringDetailsError || !recurringDetails.flexibleWorkingHours) {
        throw new Error("could not get the user's recurring flexible working hours");
    }
    if (recurringDetails.flexibleWorkingHours?.segments?.length === 0) {
        const occurringFlexibleWorkingHours = [] as FlexibleWorkingHoursView[];
        const recurringFlexibleWorkingHours = recurringDetails.flexibleWorkingHours;
        return {
            summaries: [],
            recurringFlexibleWorkingHours,
            occurringFlexibleWorkingHours,
        };
    }

    let hasINSERT_RECURRING_DAY = false;
    if (recurringDetails && flexibleWorkingHours.length > 0) {
        // we need to make sure that all recurring days appear in occurring days
        recurringDetails?.flexibleWorkingHours?.segments?.forEach(
            (recurringSegment: FlexibleWorkingHours) => {
                const recurringDay = getDay(userDate(recurringSegment.start.dateTime));
                // if we do not find a matching day for recurring and occurring
                if (
                    !modifiedOccurringFlexibleWorkingHours.some(
                        occurringSegment =>
                            getDay(userDate(occurringSegment.start.dateTime)) == recurringDay
                    )
                ) {
                    // the recurring date may not be in the current week context so we need to grab the day in the context of the current week
                    const beginningOfWeek = queryStartDate;
                    const recurringDateToAddToOccurringData = addDays(
                        beginningOfWeek,
                        recurringDay
                    );

                    // artificially inserting a day to ensure that recurring days are shown in occurring work plan
                    hasINSERT_RECURRING_DAY = true;
                    modifiedOccurringFlexibleWorkingHours.push({
                        id: 'INSERT_RECURRING_DAY',
                        workLocationType: 'Unspecified',
                        start: {
                            dateTime: getISOString(recurringDateToAddToOccurringData),
                            timeZone: {
                                name: recurringDateToAddToOccurringData.tz,
                            },
                        },
                        end: {
                            dateTime: getISOString(
                                addMinutes(recurringDateToAddToOccurringData, 30)
                            ), // end time cannot be the same as the start time
                            timeZone: {
                                name: recurringDateToAddToOccurringData.tz,
                            },
                        },
                        occurrenceDetails: {
                            seriesId: '',
                            seriesMasterId: '',
                        },
                        isConfirmed: false,
                        workLocationDetails: null,
                    });
                }
            }
        );
    }

    const recurringFlexibleWorkingHours: ListFlexibleWorkingHoursResponse =
        recurringDetails.flexibleWorkingHours;

    const occurringFlexibleWorkingHours: FlexibleWorkingHoursView[] =
        modifiedOccurringFlexibleWorkingHours;

    // If we respond to notifications containing INSERT_RECURRING_DAY, we mess up the swimlanes and
    // they end up showing "not specified" values. This seems to happen when the operation is called
    // with the current day as range.
    const summaries = hasINSERT_RECURRING_DAY
        ? []
        : getFlexibleWorkingHoursSummaries(occurringFlexibleWorkingHours);

    return {
        summaries,
        recurringFlexibleWorkingHours,
        occurringFlexibleWorkingHours,
    };
}
